import { ReactElement, ReactNode, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import * as page from "../pages";
import * as admin from "../pages/admin";
import TopBarLayout from "../components/layouts/TopBarLayout";
import FullLayout from "../components/layouts/FullLayout";
import { authStore } from "../App";
import {
  createMenuitemHirarchy,
  flattenHierarchy,
  Menuitem,
} from "../model/System";
import { AuthService } from "../services/AuthService";
import { getValidToken } from "../services/HttpService";
import { MenuitemStatus } from "../model/General";
import { Tree } from "../model";
import { DcpAuthProvider } from "../contexts/DcpAuthContext";
import * as poster from "../pages/public/poster/pagesNew";
import * as app from "../pages/public/app_base/index";
import { LADCP } from "../pages/admin/manage/license-agreement/LADCP";
import { LicenseAgreementList } from "../pages/admin/manage/license-agreement/LicenseAgreementList";

type module =
  | ""
  | "Admin"
  | "OIDC"
  | "Medlem"
  | "Timeføring"
  | "Plakat/Nyhetsbrev"
  | "App/Device"
  | "Lønnsstatistikk"
  | "DCP";

interface RoutePage {
  module: module; // A module is a group of pages that is included when it's owned by a tenant
  name?: string; // used when selecting from MenuSettings
  pathGroups?: string[]; // pages can be grouped under multiple Routes layers, this will help construct the full path when performing navigation
  path: string;
  page: JSX.Element;
  autoInclude?: boolean; // routes that are part of a module but does not have its own navigation menu
}

// DO NOT CHANGE PATH OF EXISTING PAGES
// if you do, remember to rename every occurance in sys_menuitem.url to mach the new name.
//
// all pages that require a valid access token from oidc
export const AuthRoutes: RoutePage[] = [
  { module: "", name: "Hjem", path: "/home", page: <page.Home /> },
  { module: "", name: "Profil", path: "/profile", page: <page.Profile /> },
  { module: "", name: "Bruker-innstillinger", path: "/Settings", page: <page.Settings /> },
  { module: "", name: "Tom side", path: "/emptypage", page: <page.EmptyPage /> },

  {
    module: "Admin",
    name: "Tabelloversikt",
    path: "/admin/table",
    page: <admin.TableOverview />,
  },
  {
    module: "Admin",
    name: "Kontakt administrasjon",
    path: "/admin/manageContacts",
    page: <admin.ManageContacts />,
  },
  {
    module: "Admin",
    name: "Medlems administrasjon",
    path: "/admin/manageMembers",
    page: <admin.ManageMembers />,
  },
  {
    module: "Admin",
    name: "Bruker administrasjon",
    path: "/admin/manageUsers",
    page: <admin.ManageUsers />,
  },
  {
    module: "Admin",
    name: "Bedrift administrasjon",
    path: "/admin/manageCompanies",
    page: <admin.ManageCompanies />,
  },
  // {
  //   module: "Admin",
  //   name: "Organisasjon administrasjon",
  //   path: "/admin/manageOrganizations",
  //   page: <admin.ManageOrganizations />,
  // },
  {
    module: "Admin",
    name: "Side-innstillinger",
    path: "/admin/settings",
    page: <admin.AdminSettings />,
  },
  {
    module: "Admin",
    name: "Databasestatus",
    path: "/admin/databasestate",
    page: <admin.DatabaseState />,
  },
  {
    module: "Admin",
    name: "Betalingmåte administrasjon",
    path: "/admin/order",
    page: <admin.ManagePaymentprofiles />,
  },
  {
    module: "Admin",
    name: "E-post instilling administrasjon",
    path: "/admin/manageEmailSettings",
    page: <admin.ManageEmailSettings />,
  },
  {
    module: "Admin",
    name: "E-post leverandør administrasjon",
    path: "/admin/manageEmailProviders",
    page: <admin.ManageEmailProviders />,
  },
  {
    module: "Admin",
    name: "Test send e-post",
    path: "/admin/sendMailDefault",
    page: <admin.SendMailDefault />,
  },
  {
    module: "Admin",
    name: "App device setting",
    path: "/admin/ManageAppDevices",
    page: <admin.ManageAppDevices />,
  },

  {
    module: "Medlem",
    name: "Organisasjon",
    path: "/organisasjon",
    page: <page.Organisasjon />,
  },
  { module: "Medlem", name: "Medlem", path: "/medlem", page: <page.Medlem /> },
  {
    module: "Medlem",
    name: "Bedrift",
    path: "/bedrift",
    page: <page.Bedrift />,
  },
  {
    module: "Medlem",
    name: "Tillitsverv",
    path: "/tillitsverv",
    page: <page.Tillitsverv />,
  },
  {
    module: "Medlem",
    name: "Stipend",
    path: "/stipend",
    page: <page.Stipend />,
  },
  { module: "Medlem", name: "Tariff", path: "/tariff", page: <page.Tariff /> },
  {
    module: "Medlem",
    name: "Økonomi",
    path: "/økonomi",
    page: <page.Økonomi />,
  },
  {
    module: "Medlem",
    name: "Kommunikasjon",
    path: "/kommunikasjon",
    page: <page.Kommunikasjon />,
  },
  {
    module: "Medlem",
    name: "Endre Betalingsmåte",
    path: "/changePayment",
    page: <page.ChangePayment />,
  },

  {
    module: "Timeføring",
    name: "Timeregistrering",
    path: "/hour/hourRegistration",
    page: <page.HourReg />,
  },
  {
    module: "Timeføring",
    name: "Mine timer",
    path: "/hour/myHours",
    page: <page.MyHours />,
  },
  {
    module: "Timeføring",
    name: "Aksepterte timer",
    path: "/hour/hourAccepted",
    page: <page.HourAccepted />,
  },
  {
    module: "Timeføring",
    name: "Historikk",
    path: "/hour/hourHistory",
    page: <page.HoursHistory />,
  },
  {
    module: "Timeføring",
    name: "Godkjente timer",
    path: "/hour/HourApproval",
    page: <page.HourApproval />,
  },
  {
    module: "Timeføring",
    name: "Oversikt",
    path: "/hour/HourOverview",
    page: <page.HourOverview />,
  },
  {
    module: "Timeføring",
    name: "Prosjektrapport",
    path: "/hour/ProjectReports",
    page: <page.ProjectReports />,
  },
  {
    module: "Timeføring",
    name: "Timeoverføring",
    path: "/hour/HourTransfer",
    page: <page.HourTransfer />,
  },
  {
    module: "Timeføring",
    name: "Synkroniser data",
    path: "/hour/HourSyncData",
    page: <page.HourSyncData />,
  },

  {
    module: "Plakat/Nyhetsbrev",
    name: "Plakat",
    pathGroups: ["/new/poster"],
    path: "/poster",
    page: <poster.PosterPoster />,
  },
  {
    module: "Plakat/Nyhetsbrev",
    name: "Nyhetsbrev",
    pathGroups: ["/new/poster"],
    path: "/newsletter",
    page: <poster.PosterNewsletter />,
  },
  {
    module: "Plakat/Nyhetsbrev",
    name: "Maler",
    pathGroups: ["/new/poster"],
    path: "/template",
    page: <poster.PosterTemplate />,
  },
  {
    module: "Plakat/Nyhetsbrev",
    pathGroups: ["/new/poster"],
    path: "/create-template/:pageType/:title",
    page: <poster.CreateTemplate />,
    autoInclude: true,
  },
  {
    module: "Plakat/Nyhetsbrev",
    pathGroups: ["/new/poster"],
    path: "/create/:pageType/:title/:templateID",
    page: <poster.CreatePage />,
    autoInclude: true,
  },
  {
    module: "Plakat/Nyhetsbrev",
    pathGroups: ["/new/poster"],
    path: "/edit/:type/:id",
    page: <poster.Edit />,
    autoInclude: true,
  },
  {
    module: "DCP",
    path: "/license-agreement/list",
    name: "License Agreement",
    page: <LicenseAgreementList />,
  },
  {
    module: "Lønnsstatistikk",
    name: "Lønnsstatistikk",
    path: "/user/salerystatistics",
    page: <page.MemberStatistic />,
  },
  {
    module: "Lønnsstatistikk",
    name: "Administrer lønnsstatistikk",
    path: "/manage/salerystatistics",
    page: <page.SaleryStatistics />,
  },

  {
    module: "DCP",
    path: "/license-agreement/dcp",
    name: "DCP",
    page: <LADCP />,
  },

  
  {
    module: "App/Device",
    name: "App device settings",
    pathGroups: ["/app/device"],
    path: "/ManageAppDevices",
    page: <app.ManageAppDevices />,
  },
  {
    module: "App/Device",
    name: "App message settings",
    pathGroups: ["/app/device"],
    path: "/ManageAppMessages",
    page: <app.ManageAppMessages />,
    autoInclude: true,
  },
  {
    module: "App/Device",
    name: "App chat settings",
    pathGroups: ["/app/device"],
    path: "/ManageAppChat",
    page: <app.ManageAppChat />,
    autoInclude: true,
  },
  {
    module: "App/Device",
    name: "App content settings",
    pathGroups: ["/app/device"],
    path: "/ManageAppContent",
    page: <app.ManageAppContent />,
    autoInclude: true,
  },
  {
    module: "App/Device",
    name: "App link settings",
    pathGroups: ["/app/device"],
    path: "/ManageAppLinks",
    page: <app.ManageAppLinks />,
    autoInclude: true,
  },
  {
    module: "App/Device",
    name: "App settings",
    pathGroups: ["/app/device"],
    path: "/AppSettings",
    page: <app.AppSettings />,
    autoInclude: true,
  },

];

const checkForDuplicates = (items: RoutePage[]) => {
  const nameSet = new Set<string>();
  const pathSet = new Set<string>();

  for (const item of items) {
    if (item.name) {
      if (nameSet.has(item.name)) {
        throw new Error(`Duplicate name found in AuthRoutes: "${item.name}"`);
      }
    }
    if (pathSet.has(item.path)) {
      throw new Error(`Duplicate path found in AuthRoutes: "${item.path}"`);
    }

    nameSet.add(item.name);
    pathSet.add(item.path);
  }
};

checkForDuplicates(AuthRoutes);

export const createNavigationPath = (path: string): string => {
  const route = AuthRoutes.find((r) => r.path === path);
  if (route) {
    if (route.pathGroups) {
      return route.pathGroups.join("") + route.path;
    }
    return route.path;
  }
  return "/home";
};

export const AuthenticatedRoutes = () => {
  const { isLoggedin, initialAuthLoad, mainRoutes, profileRoutes } =
    authStore();
  const { authenticate, logout } = AuthService();
  const navigate = useNavigate();

  useEffect(() => {
    authenticate(true).then((success) => {
      if (!success) {
        logout().then((logoutSuccess) => {
          if (!logoutSuccess) {
            navigate("/?authenticate=false", { replace: true });
          }
        });
      }
    });
  }, []);

  useEffect(() => {
    const getMenuItems = async () => {
      let token = await getValidToken(false);

      if (!token) {
        authStore.setState({
          mainRoutes: [],
          profileRoutes: [],
          mainRoutesTree: new Tree([]),
        });
        return;
      }

      // asume authenticated if token exisits
      const response = await fetch("/api/menuitems", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.ok) {
        let res = await response.json();

        const menuitems = res?.routes;

        const mainRoutes: Menuitem[] =
          menuitems?.filter(
            (route) => route.status === MenuitemStatus.Main
          ) ?? [];
        const profileRoutes: Menuitem[] =
          menuitems?.filter(
            (route) => route.status === MenuitemStatus.Profile
          ) ?? [];

        const mainRoutesTree = new Tree(mainRoutes);

        authStore.setState({
          mainRoutes: createMenuitemHirarchy(mainRoutes),
          profileRoutes: createMenuitemHirarchy(profileRoutes),
          mainRoutesTree: mainRoutesTree,
        });
      } else {
        authStore.setState({
          mainRoutes: [],
          profileRoutes: [],
          mainRoutesTree: new Tree([]),
        });
      }

      authStore.setState({ initialAuthLoad: true });
    };

    getMenuItems();
  }, []);

  function constructAuthRoutes(menuitems: Menuitem[]): ReactElement[] {
    let availableRoutes: ReactElement[] = [];
    let posterRoutes: ReactElement[] = [];
    let appRoutes: ReactElement[] = [];
    let unique: RoutePage[] = [];

    if (menuitems) {
      let flatMenuitems = flattenHierarchy(menuitems);

      flatMenuitems.forEach((menuitem) => {
        const RoutePage = AuthRoutes.find((r) => r.path === menuitem.url);
        if (RoutePage) {

          const path = menuitem.url;
          let exists = unique.find(u => u.name === RoutePage.name);

          if (!exists) {
            if (RoutePage.module === "Plakat/Nyhetsbrev") {
              posterRoutes.push(
                <Route
                  key={menuitem.id}
                  path={path}
                  element={RoutePage.page}
                />
              );
            } else if (RoutePage.module === "App/Device") {
              appRoutes.push(
                <Route
                  key={menuitem.id}
                  path={path}
                  element={RoutePage.page}
                />
              );
            } else
             {
              availableRoutes.push(
                <Route
                  key={menuitem.id}
                  path={path}
                  element={RoutePage.page}
                />
              );
            }

            unique.push(RoutePage);
          }
        }
      });
    }

    let highestId = 0;
    // include routes with autoInclude flag for the Plakat/Nyhetsbrev module
    if (posterRoutes.length > 0) {
      const autoRoutes = AuthRoutes.filter(
        (r) => r.module === "Plakat/Nyhetsbrev"
      ).filter((r) => r.autoInclude);
      highestId = menuitems.reduce(
        (max, item) => (item.id > max ? item.id : max),
        0
      );
      autoRoutes.forEach((autoRoute) => {
        highestId += 1;
        posterRoutes.push(
          <Route
            key={highestId}
            path={autoRoute.path}
            element={autoRoute.page}
          />
        );
      });
      posterRoutes.push(
        <Route key={highestId + 1} path="/*" element={<page.NotFound />} />
      );

      // add poster routes under group
      availableRoutes.push(
        <Route
          key={highestId + 2}
          path="/new/poster/*"
          element={
            <DcpAuthProvider>
              <Routes>{posterRoutes}</Routes>
            </DcpAuthProvider>
          }
        />
      );
    }


    if (appRoutes.length > 0) {
      const autoRoutes = AuthRoutes.filter(
        (r) => r.module === "App/Device"
      ).filter((r) => r.autoInclude);
      // let highestId = menuitems.reduce(
      //   (max, item) => (item.id > max ? item.id : max),
      //   0
      // );
      autoRoutes.forEach((autoRoute) => {
        highestId += 1;
        appRoutes.push(
          <Route
            key={highestId}
            path={autoRoute.path}
            element={autoRoute.page}
          />
        );
      });
      appRoutes.push(
        <Route key={highestId + 1} path="/*" element={<page.NotFound />} />
      );

      // add poster routes under group
      availableRoutes.push(
        <Route
          key={highestId + 2}
          path="/app/device/*"
          element={
            <Routes>{appRoutes}</Routes>
          }
        />
      );
    }



    return availableRoutes;
  }

  if (!isLoggedin || !initialAuthLoad) {
    return null;
  }

  return (
    <>
      <TopBarLayout>
        <Routes>
          <Route path="/home" element={<page.Home />} />
          <Route path="/emptypage/:pagename" element={<page.EmptyPage />} />

          {constructAuthRoutes([...mainRoutes, ...profileRoutes])}

          {/* <Route path="/new/poster/*" element={
            <DcpAuthProvider>
              <Routes>
                <Route path="/poster" element={<poster.PosterPoster />} />
              </Routes>
            </DcpAuthProvider>
          } /> */}

          {/** @todo: on prod, replace these with <Route path="/*" element={<Navigate to='/' replace />} /> */}
          <Route path="/:path" element={<page.NotFound />} />
          <Route path="/*" element={<page.NotFound />} />
        </Routes>
      </TopBarLayout>
    </>
  );
};

const AdminRoutes = () => {
  const { userRoles } = authStore();
  return (
    <Routes>
      {userRoles.includes("admin") ? (
        <>
          <Route path="/table" element={<admin.TableOverview />} />
          <Route path="/manageMembers" element={<admin.ManageMembers />} />
          <Route path="/settings" element={<admin.AdminSettings />} />
          <Route path="/databasestate" element={<admin.DatabaseState />} />
          <Route path="/*" element={<Navigate to="table" replace />} />
        </>
      ) : (
        <Route path="/*" element={<page.MissingPermission />} />
      )}
    </Routes>
  );
};
